import styled from '@emotion/styled';
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { createRef, Component, Fragment } from 'react';

import AppliedFiltersList from './filters/AppliedFilter';
import Filter from './filters/Filter';
import Banner from './items/Banner';
import { setParams } from '../../helpers/params';
import windowWidth from '../../helpers/window_width';
import { connectLocation } from '../../location/LocationConnectors';
import { H2, H3 } from '../oc-h';

const ContainerHeader = styled.div`
  border-bottom: 1px solid;
  display: inline-flex;
  justify-content: space-between;
  padding-bottom: 0.75em;
  position: relative;
  width: 100%;
`;

class FilterList extends Component {
  static propTypes = {
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    filters: PropTypes.arrayOf(PropTypes.object).isRequired,
    filterFunc: PropTypes.func.isRequired,
    itemComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]).isRequired,
    itemKeyFactory: PropTypes.func.isRequired, // signature: (item) => item.uid
    itemPluralizationFunc: PropTypes.func, // signature: ({mobile, count, t}) => ""
    baseHierarchy: PropTypes.string,
    banners: PropTypes.arrayOf(PropTypes.object),
    externalFilters: PropTypes.object,
    customFilterFactories: PropTypes.object, // format: { "ComponentNameInFilterData": (props) => <SomeFilterComponent /> }
    t: PropTypes.func.isRequired, // connectLocation
  };

  constructor(props) {
    super(props);
    this.state = {
      filters: props.filters.filter(f => !f.hierarchical),
      hierarchical: props.filters.find(f => f.hierarchical),
      baseHierarchy: props.baseHierarchy || '',
      banners: {},
      filtersOpenOnMobile: false,
    };
    this.onChangeFilterOption = this.onChangeFilterOption.bind(this);
    this.onChangeHierarchy = this.onChangeHierarchy.bind(this);
    this.onToggleFilter = this.onToggleFilter.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.toggleFiltersOnMobile = this.toggleFiltersOnMobile.bind(this);
    this.toggleFiltersOnMobileKeyboard = this.toggleFiltersOnMobileKeyboard.bind(this);
    this.filterItems = this.filterItems.bind(this);
    this.filtersCloseRef = createRef();
    this.scrollToRef = createRef();

    if (props.banners) {
      this.state.banners = props.banners.reduce((acc, b) => {
        const inPosition = acc[b.position] || [];
        acc[b.position] = [...inPosition, b];
        return acc;
      }, {});
    }

    if (
      this.state.filters.find(f => f.selected.length > 0) ||
      (this.state.hierarchical && this.state.hierarchical.selected !== this.state.baseHierarchy)
    ) {
      this.props.filtersInteraction && this.props.filtersInteraction();
    }
  }

  filterItems() {
    return this.props.filterFunc({
      items: this.props.items,
      filters: this.state.filters,
      hierarchical: this.state.hierarchical,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.externalFilters != prevProps.externalFilters) {
      this.refreshFilters();
    }
  }

  componentDidMount() {
    this.filtersModal = document.getElementById('filters');
  }

  componentWillUnmount() {
    clearAllBodyScrollLocks();
  }

  refreshFilters() {
    const { externalFilters } = this.props;
    const { hierarchical, filters } = externalFilters;

    if (hierarchical) {
      const key = Object.keys(hierarchical)[0];
      if (key && hierarchical[key]) {
        this.onChangeHierarchy(hierarchical[key].slug);
      }
    }
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          this.onChangeFilterOption(key, [filters[key].slug]);
        }
      });
    }
  }

  updateQueryParams() {
    const { filters, hierarchical } = this.state;
    let paramsFilters = filters.filter(f => f).reduce((acc, f) => ({ ...acc, [f.param_name]: f.selected }), {});
    if (hierarchical && !hierarchical.extraProps.hierarchyInPath) {
      paramsFilters = {
        ...paramsFilters,
        [hierarchical.param_name]: hierarchical.selected,
      };
    }
    setParams(paramsFilters);
  }

  updatePath() {
    const { search } = document.location;
    const { selected: path } = this.state.hierarchical;
    const pathname = `${this.props.baseUrl}/${path}${search}`;
    history.replaceState(null, document.title, pathname);
  }

  onChangeFilterOption(filterUid, values) {
    this.props.filtersInteraction && this.props.filtersInteraction();
    const ref = this.scrollToRef.current;
    window.scrollTo({
      behavior: 'smooth',
      top: ref.offsetTop + ref.offsetHeight,
    });

    const filter = this.state.filters.find(f => f.uid === filterUid);
    // Overwrite `selected` prop
    const updatedFilter = { ...filter, selected: values };
    // Replace a single element in array using reduce
    const newFilters = this.state.filters.reduce((acc, f) => [...acc, f.uid === filterUid ? updatedFilter : f], []);

    this.setState({ filters: newFilters }, () => this.updateQueryParams());
  }

  onChangeHierarchy(value) {
    this.props.filtersInteraction && this.props.filtersInteraction();
    // If hierarchy is kept in path we need to update it instead of params
    const callback = () => {
      this.props.onChangeHierarchy && this.props.onChangeHierarchy(this.state.hierarchical.selected);
      this.state.hierarchical.extraProps.hierarchyInPath ? this.updatePath() : this.updateQueryParams();
    };
    this.setState({ hierarchical: { ...this.state.hierarchical, selected: value } }, callback);
  }

  resetFilters() {
    this.props.filtersReset && this.props.filtersReset();
    const emptyFilters = this.state.filters.map(f => ({ ...f, selected: [] }));
    let emptyHierarchical = null;
    if (this.state.hierarchical) {
      emptyHierarchical = {
        ...this.state.hierarchical,
        selected: this.state.baseHierarchy,
      };
    }
    this.setState(
      {
        filters: emptyFilters,
        hierarchical: emptyHierarchical,
      },
      () => {
        this.updatePath();
        this.updateQueryParams();
      }
    );
  }

  prerolls() {
    const { top = [] } = this.state.banners;

    return top.filter(b => this.showBanner(b)).map((banner, i) => <Banner key={i} {...banner} />);
  }

  injectInterstitial(i) {
    const { interstitial = [] } = this.state.banners;
    const banner = interstitial[i];

    if (!banner) return null;

    const visible = this.showBanner(banner);

    if (!visible) return null;

    return <Banner key={i} {...banner} />;
  }

  postrolls() {
    const { bottom = [] } = this.state.banners;

    return bottom.filter(b => this.showBanner(b)).map((banner, i) => <Banner key={i} {...banner} />);
  }

  showBanner(banner) {
    const { visibleIf, visibleIfHierarchyMatch, visibleIfLocale, visibleIfNotLocale } = banner;
    let visible = true;

    if (this.state.hierarchical && visibleIfHierarchyMatch) {
      visible = (this.state.hierarchical.selected || '').match(RegExp(visibleIfHierarchyMatch.replace(/^\/|\/$/g, '')));
    }

    if (visible && visibleIf) {
      visible = Object.entries(visibleIf).every(([key, values]) => {
        const filter = this.state.filters.find(filter => filter.param_name === key);
        return values.find(v => JSON.stringify(v) == JSON.stringify(filter.selected));
      });
    }

    if (visible && visibleIfLocale) visible = visibleIfLocale.some(locale => this.props.locale.code === locale);
    if (visible && visibleIfNotLocale) visible = !visibleIfNotLocale.some(locale => this.props.locale.code === locale);

    return visible;
  }

  onToggleFilter({ component, path, uid, slug }) {
    if (component === 'HierarchicalFilter') {
      if (this.state.hierarchical && this.state.hierarchical.selected == path) {
        this.onChangeHierarchy(this.state.baseHierarchy);
      } else {
        this.onChangeHierarchy(path);
      }
    } else {
      let selected = [...this.state.filters.find(f => f.uid === uid).selected];
      const idx = selected.indexOf(slug);
      if (idx >= 0) {
        selected = selected.filter(v => v !== slug);
      } else {
        selected = [...selected, slug];
      }
      this.onChangeFilterOption(uid, selected);
    }
  }

  get appliedFilters() {
    const result = this.state.filters.flatMap(f =>
      f.selected.map(af => ({
        component: f.component,
        uid: f.uid,
        slug: af,
        title: f.options.find(opt => opt.slug == af).proper_name,
      }))
    );
    if (this.state.hierarchical && this.state.hierarchical.selected) {
      let appliedFilterName = 'missing proper name';
      try {
        if (this.state.hierarchical.extraProps?.slugInAppliedFilters) {
          appliedFilterName = this.state.hierarchical.selected;
        } else {
          // reduce the split slugs array by digging into hierarchical.options and children
          appliedFilterName = this.state.hierarchical.selected
            .split('/')
            .filter(s => s)
            .reduce(
              ([node, root], slug) => {
                const new_slug = root + (root ? '/' : '') + slug;
                return [node.children.find(opt => opt.slug == new_slug), new_slug];
              },
              [{ children: this.state.hierarchical.options }, '']
            )[0].proper_name;
        }
      } catch (e) {
        console.log(e);
      }
      result.push({
        component: 'HierarchicalFilter',
        path: this.state.hierarchical.selected,
        title: appliedFilterName,
      });
    }
    return result;
  }

  toggleFiltersOnMobile() {
    this.setState({ filtersOpenOnMobile: !this.state.filtersOpenOnMobile }, () => {
      if (this.state.filtersOpenOnMobile) {
        disableBodyScroll(this.filtersModal);
        {
          /* Focus needs to be set after rendering is completed, use requestAnimationFrame */
        }
        window.requestAnimationFrame(() => this.filtersCloseRef.current.focus());
      } else {
        enableBodyScroll(this.filtersModal);
        this.scrollToRef.current.focus();
      }
    });
  }

  toggleFiltersOnMobileKeyboard(event) {
    if (event.key === 'Enter' || event.key === 'Space' || event.key === ' ') {
      this.toggleFiltersOnMobile();
    }
  }

  render() {
    const { t, itemKeyFactory } = this.props;
    const selectedItems = this.filterItems();
    const { appliedFilters } = this;
    const accessibleFiltersProps =
      windowWidth() < 1200
        ? {
            role: 'dialog',
            'aria-modal': true,
            'aria-label': t('product_list.filters'),
            'aria-hidden': !this.state.filtersOpenOnMobile,
          }
        : {};

    return (
      <div className="container product-list">
        <div className="heading-container">
          {/* scrollto reference https://stackoverflow.com/questions/43441856/how-to-scroll-to-an-element */}
          <H2 role="status" tabIndex="0" ref={this.scrollToRef}>
            {this.props.itemPluralizationFunc &&
              this.props.itemPluralizationFunc({
                mobile: false,
                count: selectedItems.length,
                t,
              })}
          </H2>
        </div>
        <section className="hide-desktop no-margin-bottom">
          <div className="filter-tab-container">
            <a
              className="filter-trigger oc-icon"
              tabIndex="0"
              onClick={this.toggleFiltersOnMobile}
              onKeyDown={this.toggleFiltersOnMobileKeyboard}
            >
              <i className="icon filters" />
              <span>{appliedFilters.length > 0 ? `Filters (${appliedFilters.length})` : 'Filters'}</span>
            </a>
          </div>
        </section>
        <div className="columns ratio-1-3">
          <section
            id="filters"
            className={`col filterContainer only-desktop ${this.state.filtersOpenOnMobile ? 'visible-mobile' : ''}`}
            data-am-region="Product Filter Side Panel"
            {...accessibleFiltersProps}
          >
            <div className="filter-sidebar-header hide-desktop">
              <H3 className="mobile-heading">
                {this.props.itemPluralizationFunc &&
                  this.props.itemPluralizationFunc({
                    mobile: true,
                    count: selectedItems.length,
                    t,
                  })}
              </H3>
              <div className="filter-close-container">
                <a
                  className="filter-close"
                  tabIndex="0"
                  ref={this.filtersCloseRef}
                  onClick={this.toggleFiltersOnMobile}
                  onKeyDown={this.toggleFiltersOnMobileKeyboard}
                >
                  &times;
                </a>
              </div>
            </div>
            {this.state.filters && (
              <>
                <ContainerHeader>
                  <H3 className="only-desktop">{t('product_list.filters')}</H3>
                </ContainerHeader>
                <div className="filteringSidebar">
                  {/* Yaay let's filter out the nonexistent hierarchical filter */}
                  {_.sortBy(
                    [this.state.hierarchical, ...this.state.filters].filter(f => f),
                    'sortOrder'
                  ).map(filter => (
                    <Filter
                      key={filter.uid}
                      title={filter.title}
                      uid={filter.uid}
                      component={filter.component}
                      options={filter.options}
                      selected={filter.selected}
                      extraProps={filter.extraProps}
                      onChange={v => this.onChangeFilterOption(filter.uid, v)}
                      onHierarchicalChange={this.onChangeHierarchy}
                      filterGroupCount={this.state.filters.length + 1}
                      baseHierarchy={this.props.baseHierarchy}
                      rootHierarchy={this.props.rootHierarchy}
                      currentHierarchy={this.state.hierarchical && this.state.hierarchical.selected}
                      filters={this.state.filters}
                      hierarchical={this.state.hierarchical}
                      items={this.props.items}
                      filterFunc={this.props.filterFunc}
                      customFilterFactories={this.props.customFilterFactories}
                    />
                  ))}
                </div>
              </>
            )}
            <div className="filter-control hide-desktop">
              <a
                className="control"
                tabIndex="0"
                onClick={this.toggleFiltersOnMobile}
                onKeyDown={this.toggleFiltersOnMobileKeyboard}
              >
                {t('product_list.close_filters')}
              </a>
              <a className="control" tabIndex="0" onClick={this.resetFilters}>
                {t('product_list.reset_filters')}
              </a>
              <a
                className="control apply-filter"
                tabIndex="0"
                onClick={this.toggleFiltersOnMobile}
                onKeyDown={this.toggleFiltersOnMobileKeyboard}
              >
                <span>{t('product_list.apply_filters')}</span>
                <br />
                <span>{`(${selectedItems.length})`}</span>
              </a>
            </div>
          </section>
          <section className="col" data-am-region="Product Listings">
            <AppliedFiltersList
              filters={appliedFilters}
              onToggleFilter={this.onToggleFilter}
              reset={this.resetFilters}
            />
            <div className={`col-md-9 ${this.props.className} product-list`}>
              <div className={`${this.props.className}product-list__container`}>
                {this.prerolls()}
                {selectedItems.map((item, i) => (
                  <Fragment key={itemKeyFactory(item)}>
                    {this.props.itemComponent(item, i)}
                    {i % 3 === 2 ? this.injectInterstitial(Math.floor(i / 3)) : null}
                  </Fragment>
                ))}
                {this.postrolls()}
              </div>
            </div>
          </section>
        </div>

        <div className="row">
          <div className={`filter-hide-overlay ${this.props.showOverlay ? 'active' : ''}`} />
        </div>
      </div>
    );
  }
}

FilterList = connectLocation(FilterList);

export default FilterList;
